import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '~/libs/api';
import { parseMultipartData } from '~/helpers/utils';
import type {
  TApiApplicationVersionResponse,
  TApiApplicationDocumentFile,
  TApiReadyCovenantDocumentsResponse,
  TApiReadyCovenantsResponse,
  TApiSupervisorApplication,
  TApiSupervisorApplicationDocumentsResponse,
  TApiSupervisorApplicationsResponse,
} from '~/modules/covenants/types.ts';
import { thunkErrorHandler } from '~/store/utils.ts';
import type {
  TAcceptSupervisorApplicationRequest,
  TSupervisorApplicationAssignmentRequest,
  TSupervisorApplicationReadyCovenantDocumentsRequest,
  TSupervisorApplicationReadyCovenantsRequest,
  TSupervisorApplicationDocumentsRequest,
  TUpdateSupervisorApplicationDocumentFileCommentRequest,
  TUpdateSupervisorApplicationDocumentsCommentRequest,
  TSupervisorApplicationRequest,
  TSupervisorApplicationsRequest,
  TThunkError,
} from '~/store/covenants/types.ts';
import { buildSupervisorApplicationsParams } from '~/store/covenants/thunks/utils.ts';

export const fetchSupervisorApplication = createAsyncThunk<
  TApiSupervisorApplication,
  TSupervisorApplicationRequest,
  { rejectValue: TThunkError }
>('covenantsPage/fetchSupervisorApplication', async ({ applicationId }, { rejectWithValue }) => {
  try {
    const { data } = await api.get(
      `/covenants-api/covenants/supervisors/applications/${applicationId}`
    );

    return data;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const fetchSupervisorApplications = createAsyncThunk<
  TApiSupervisorApplicationsResponse,
  TSupervisorApplicationsRequest,
  { rejectValue: TThunkError }
>('covenantsPage/fetchSupervisorApplications', async (payload, { rejectWithValue }) => {
  try {
    const requestParams = payload;
    const { data } = await api.get('/covenants-api/covenants/supervisors/applications', {
      params: buildSupervisorApplicationsParams(requestParams),
    });

    return {
      supervisorApplications: data.supervisorApplications ?? [],
      count: data.pageConfiguration?.totalElements ?? data.supervisorApplications?.length ?? 0,
      pageConfiguration: data.pageConfiguration ?? {
        page: requestParams.page,
        size: requestParams.size,
        totalElements: data.supervisorApplications?.length ?? 0,
      },
    };
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const exportSupervisorApplications = createAsyncThunk<
  { data: Blob; filename: string; contentType?: string },
  TSupervisorApplicationsRequest,
  { rejectValue: TThunkError }
>('covenantsPage/exportSupervisorApplications', async (payload, { rejectWithValue }) => {
  try {
    const response = await api.get<Blob>(
      '/covenants-api/covenants/supervisors/applications/export',
      {
        headers: {
          Accept: '*/*',
        },
        params: buildSupervisorApplicationsParams(payload),
        responseType: 'blob',
      }
    );

    const responseContentType = response.headers['content-type'];
    const contentType =
      typeof responseContentType === 'string' && responseContentType.trim()
        ? responseContentType
        : undefined;
    const contentDisposition = response.headers['content-disposition'];
    const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
    const filename = filenameMatch ? filenameMatch[1] : 'supervisor_applications_report.xlsx';

    return {
      data: response.data,
      filename,
      contentType,
    };
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const fetchSupervisorApplicationReadyCovenants = createAsyncThunk<
  TApiReadyCovenantsResponse,
  TSupervisorApplicationReadyCovenantsRequest,
  { rejectValue: TThunkError }
>(
  'covenantsPage/fetchSupervisorApplicationReadyCovenants',
  async ({ applicationId, page = 0, size = 10 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/covenants-api/covenants/supervisors/applications/${applicationId}/covenants/ready`,
        {
          params: {
            page,
            size,
          },
        }
      );

      const covenants = data.covenants ?? [];
      const totalElements = data.pageConfiguration?.totalElements ?? data.count ?? covenants.length;

      return {
        covenants,
        count: data.count ?? totalElements,
        pageConfiguration: data.pageConfiguration ?? {
          page,
          size,
          totalElements,
        },
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchSupervisorApplicationReadyCovenantDocuments = createAsyncThunk<
  TApiReadyCovenantDocumentsResponse,
  TSupervisorApplicationReadyCovenantDocumentsRequest,
  { rejectValue: TThunkError }
>(
  'covenantsPage/fetchSupervisorApplicationReadyCovenantDocuments',
  async (
    { applicationId, documentIds, yearPeriod, childPeriod, deadline },
    { rejectWithValue }
  ) => {
    try {
      if (!documentIds.length) {
        return { documents: [] };
      }

      const params = new URLSearchParams();

      documentIds.forEach((documentId) => {
        params.append('documentIds', String(documentId));
      });

      if (yearPeriod?.trim()) {
        params.append('yearPeriod', yearPeriod.trim());
      }

      if (childPeriod?.trim()) {
        params.append('childPeriod', childPeriod.trim());
      }

      if (deadline?.trim()) {
        params.append('deadline', deadline.trim());
      }

      const { data } = await api.get(
        `/covenants-api/covenants/supervisors/applications/${applicationId}/covenants/ready/documents`,
        {
          params,
        }
      );

      return {
        documents: data.documents ?? [],
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchSupervisorApplicationDocuments = createAsyncThunk<
  TApiSupervisorApplicationDocumentsResponse,
  TSupervisorApplicationDocumentsRequest,
  { rejectValue: TThunkError }
>(
  'covenantsPage/fetchSupervisorApplicationDocuments',
  async ({ applicationId, page = 0, size = 10 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/covenants-api/covenants/supervisors/applications/${applicationId}/documents`,
        {
          params: {
            page,
            size,
          },
        }
      );

      return {
        applicationId,
        documents: data.documents ?? [],
        count: data.pageConfiguration?.totalElements ?? data.documents?.length ?? 0,
        pageConfiguration: data.pageConfiguration ?? {
          page,
          size,
          totalElements: data.documents?.length ?? 0,
        },
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const updateSupervisorApplicationDocumentFileComment = createAsyncThunk<
  TApiApplicationDocumentFile,
  TUpdateSupervisorApplicationDocumentFileCommentRequest,
  { rejectValue: TThunkError }
>(
  'covenantsPage/updateSupervisorApplicationDocumentFileComment',
  async ({ applicationId, fileId, comment }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(
        `/covenants-api/covenants/supervisors/applications/${applicationId}/documents/files/${fileId}`,
        { comment }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const updateSupervisorApplicationDocumentsComment = createAsyncThunk<
  void,
  TUpdateSupervisorApplicationDocumentsCommentRequest,
  { rejectValue: TThunkError }
>(
  'covenantsPage/updateSupervisorApplicationDocumentsComment',
  async ({ applicationId, comment }, { rejectWithValue }) => {
    try {
      await api.patch(
        `/covenants-api/covenants/supervisors/applications/${applicationId}/documents/files`,
        { comment }
      );
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const downloadSupervisorApplicationDocument = createAsyncThunk<
  { data: Blob; filename: string; contentType?: string },
  { applicationId: string; id: string | number },
  { rejectValue: TThunkError }
>(
  'covenantsPage/downloadSupervisorApplicationDocument',
  async ({ applicationId, id }, { rejectWithValue }) => {
    try {
      const headers = {
        Accept: 'multipart/form-data',
      };

      const path = `/covenants-api/covenants/supervisors/applications/${applicationId}/documents/${id}/download`;
      const response = await api.get<Blob>(path, {
        headers,
        responseType: 'blob',
      });

      const responseContentType = response.headers['content-type'];
      const contentType =
        typeof responseContentType === 'string' && responseContentType.trim()
          ? responseContentType
          : undefined;
      const isMultipartResponse =
        typeof contentType === 'string' && contentType.includes('multipart/');

      if (isMultipartResponse) {
        const parsedMultipart = await parseMultipartData(response);
        const file = parsedMultipart.files[0];

        if (file) {
          return {
            data: file,
            filename: file.name || `document_${id}`,
            contentType: file.type || undefined,
          };
        }
      }

      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `document_${id}`;

      return {
        data: response.data,
        filename,
        contentType,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const assignSupervisorApplication = createAsyncThunk<
  TApiApplicationVersionResponse,
  TSupervisorApplicationAssignmentRequest,
  { rejectValue: TThunkError }
>(
  'covenantsPage/assignSupervisorApplication',
  async ({ applicationId, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(
        `/covenants-api/covenants/supervisors/applications/${applicationId}/assign`,
        { version },
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const acceptSupervisorApplication = createAsyncThunk<
  void,
  TAcceptSupervisorApplicationRequest,
  { rejectValue: TThunkError }
>(
  'covenantsPage/acceptSupervisorApplication',
  async ({ applicationId, version, payload }, { rejectWithValue }) => {
    try {
      await api.post(
        `/covenants-api/covenants/supervisors/applications/${applicationId}/accept`,
        payload,
        {
          params: { version },
        }
      );
      return;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const unassignSupervisorApplication = createAsyncThunk<
  TApiApplicationVersionResponse,
  TSupervisorApplicationAssignmentRequest,
  { rejectValue: TThunkError }
>(
  'covenantsPage/unassignSupervisorApplication',
  async ({ applicationId, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(
        `/covenants-api/covenants/supervisors/applications/${applicationId}/unassign`,
        { version },
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);
